import { SHARES_MODAL_MODE } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { useWorkflowProgress } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/WorkflowProgress/WorkflowProgressContext';
import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import {
	NewJoinersAndAllocationsKycFormValues,
	NewJoinersAndAllocationsProofsStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/useNewJoinersAndAllocationsKYCController';
import {
	getLoanDefinitionValidationSchema,
	LoanDefinitionForm,
	LoanDefinitionFormValues,
} from '@dealstack/frontend/shared/components/LoanDefinitonTable/useLoanDefinitionTableController';
import { getSecuritiesValidationSchema } from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController';
import {
	getMIPDetailsDataForJoiner,
	postJoinersOrAllocationsData,
	uploadFile,
	getMIPKycFormsTemplates,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { getSecuritiesDataForMip } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import {
	getMipKycSetting,
	updateMipKycSetting,
} from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import type { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types.ts';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { isReviewSignatureType } from '@dealstack/shared/lib/common/signatures';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import {
	CreateCustomDetailDto,
	DataContractTct,
	DataCounterpartyTct,
	DataFileTct,
	PostInvestorJoinerStatistics,
	PostJoinerAdditionalAllocationMIPDto,
} from '@dealstack/shared/lib/generated';
import {
	toJoinerProofValues,
	toJoinerKycFormValues,
	toProgrammeProofUpdate,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/newJoinersAndAllocationsKycForms.utils';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { CustomDetailDataTypeEnum } from '@dealstack/shared/lib/generated/models/CustomDetailDataTypeEnum.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import pLimit from 'p-limit';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { SecuritiesForm } from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController.types';

// defaulting to 5 concurrent uploads
// this can be adjusted if needed, but 5 is a good balance between performance and server load
// it can be increased if the server can handle more concurrent uploads
const uploadConcurrencyLimit = pLimit(5);

export type NewJoinersAndAllocationsPersonalDataCustomDetailEntry = CreateCustomDetailDto & {
	id?: string;
	localId?: string;
};

export interface NewJoinersAndAllocationsPersonalDetailsFormValues {
	individual: NewJoinersAndAllocationsPersonalDataCustomDetailEntry[];
	vehicle: NewJoinersAndAllocationsPersonalDataCustomDetailEntry[];
}

export interface ContractsFormValues {
	contracts: NewJoinersAndAllocationsContractDataItem[];
}

export type NewJoinersAndAllocationsContractDataItem = Omit<
	DataContractTct,
	'mipId' | 'createdBy' | 'esignProvider'
> & {
	file?: DataFileTct;
	fileInstance?: File;
	local?: boolean;
	counterparties: Array<DataCounterpartyTct>;
	hasWitnesses?: boolean;
};

export interface NewJoinersAndAllocationsInviteTemplateFormValues {
	invitationText: string;
}

export type NewJoinersAndAllocationsInvestorLocal =
	PostJoinerAdditionalAllocationMIPDto['investorAllocations'][0] & {
		id: string;
	};

export interface INewJoinersAndAllocationsInvestorsFormValues {
	investorsListData: NewJoinersAndAllocationsInvestorLocal[];
	summary: PostInvestorJoinerStatistics;
}

const INITIAL_STEP = 0;
export const NEW_JOINERS_AND_ALLOCATIONS_MAX_STEP_NUMBER = 8;

export const useNewJoinersAndAllocationsModalController = () => {
	const t = useTranslationDefault();
	const commonYup = useCommonYupValidation();
	const tMipManage = useTranslationMipManage();
	const { startEmailWorkflow } = useWorkflowProgress();

	const putMipKycSettingRequest = useWrappedRequest(updateMipKycSetting);
	const getMipKycSettingRequest = useWrappedRequest(
		(_args: void, options?: ServiceRequestOptions) => getMipKycSetting(options)
	);
	const manageInvestorsControllerV2 = useContext(MIPManageInvestorsV2Context);
	const MIPManageController = useMIPManageContext();

	const getMIPDetailsDataRequest = useWrappedRequest(getMIPDetailsDataForJoiner);
	const postJoinersOrAllocationsDataRequest = useWrappedRequest(postJoinersOrAllocationsData);
	const uploadFileRequest = useWrappedRequest(uploadFile);
	const getKycFormsTemplatesRequest = useWrappedRequest(getMIPKycFormsTemplates);
	const getSecuritiesDataForMipRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
				mipId: MIPManageController.mipId as string,
			}),
		],
		queryFn: ({ signal }) =>
			getSecuritiesDataForMip(
				formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
					mipId: MIPManageController.mipId as string,
				}),
				undefined,
				signal
			),
		enabled: !!MIPManageController.mipId,
	});

	const [isCompleting, setIsCompleting] = useState(false);
	// Whether the programme's own proof requirements arrived. The KYC step reads it to avoid
	// presenting its all-off defaults as the programme's answer, and submit to avoid saving them.
	const [programmeProofsStatus, setProgrammeProofsStatus] =
		useState<NewJoinersAndAllocationsProofsStatus>('loading');

	const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
	const [disableContinue, setDisableContinue] = useState(false);

	const generalDealInputsForm = useForm<SecuritiesForm>({
		mode: 'all',
		resolver: yupResolver(getSecuritiesValidationSchema(t)),
	});

	const loanDefinitionForm = useForm<LoanDefinitionForm>({
		mode: 'all',
		resolver: yupResolver(getLoanDefinitionValidationSchema(t)),
	});

	const personalDataForm = useForm<NewJoinersAndAllocationsPersonalDetailsFormValues>();
	const kycForm = useForm<NewJoinersAndAllocationsKycFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues: {
			...toJoinerProofValues(getMipKycSettingRequest?.response?.payload),
			individualFormsTemplates: [],
			pivFormsTemplates: [],
		},
		resolver: yupResolver(
			Yup.object().shape({
				individualFormsTemplates: Yup.array().of(
					Yup.object().shape({
						name: commonYup.string.required,
					})
				),
				pivFormsTemplates: Yup.array().of(
					Yup.object().shape({
						name: commonYup.string.required,
					})
				),
			})
		),
	});
	const contractForm = useForm<ContractsFormValues>({
		defaultValues: {
			contracts: [],
		},
		mode: 'all',
		resolver: yupResolver(
			Yup.object().shape({
				contracts: Yup.array().of(
					Yup.object().shape({
						name: commonYup.contract_name.required,
					})
				),
			})
		),
	});
	const inviteTemplateForm = useForm<NewJoinersAndAllocationsInviteTemplateFormValues>();
	const investorsForm = useForm<INewJoinersAndAllocationsInvestorsFormValues>();

	useEffect(() => {
		const mipSecurities = getSecuritiesDataForMipRequest.data;

		const formSecurities = generalDealInputsForm.getValues().securities;
		const securities =
			mipSecurities?.map((security) => ({
				id: security.id,
				localId: security.id,
				name: security.name,
				type: security.type,
				votesPerUnit: security.votesPerUnit,
				nominalValue: security.nominalValue,
				hasTransactions: security.hasTransactions,
				isSubjectToVesting: security.isSubjectToVesting,
				locked: true,
				isOnEdit: false,
				isOnAdd: false,
			})) ?? [];

		const mergedSecurities = [
			...(formSecurities ?? []),
			...securities.filter(
				({ localId }) =>
					!formSecurities?.find((formSecurity) => localId === formSecurity.localId)
			),
		];

		generalDealInputsForm.reset({ securities: mergedSecurities });
	}, [getSecuritiesDataForMipRequest.data]);

	useEffect(() => {
		// oxlint-disable-next-line typescript/no-floating-promises
		(async () => {
			const kycSettingResponse = await getMipKycSettingRequest.fetch().concludeWithPromise();

			// KYC form templates are DocumentWorkflow-native since the Forms cutover; the
			// customDetails payload only still carries pre-cutover leftovers, and never any PIV
			// form, so reading it there left the Vehicle section permanently empty.
			const kycFormTemplates =
				(await getKycFormsTemplatesRequest.fetch({ archived: false }).concludeWithPromise())
					?.payload ?? [];

			// The KYC step reads the programme's settings and templates, not the deal details, so
			// it resets on its own. Behind the details fetch, a failure there left the step on its
			// all-false mount defaults — which submit then wrote back as the programme's proofs.
			kycForm.reset(toJoinerKycFormValues(kycFormTemplates, kycSettingResponse?.payload));
			setProgrammeProofsStatus(kycSettingResponse?.payload ? 'loaded' : 'unavailable');

			await getMIPDetailsDataRequest
				.fetch()
				.onSuccess((result) => {
					if (!result?.payload) {
						return;
					}

					const formLoansDefinitions = loanDefinitionForm.getValues(
						LoanDefinitionFormValues.loanDefinitions
					);
					const mipLoansDefinitions = result.payload.loanDefinitions.map((item) => ({
						...item,
						isOnEdit: false,
						locked: true,
						localId: item.id,
					}));
					const mergedLoansDefinitions = [
						...(formLoansDefinitions ?? []),
						...mipLoansDefinitions.filter(
							({ localId }) =>
								!formLoansDefinitions?.find(
									(formLoanDefinition) => localId === formLoanDefinition.localId
								)
						),
					];

					loanDefinitionForm.reset({ loanDefinitions: mergedLoansDefinitions });
				})
				.concludeWithPromise();
		})();
	}, []);

	const isMIP1200CaseValid = (() => {
		const investorsFormValues = investorsForm.getValues();
		// d.slaby 18.12.2023 MIP-1200
		const result = investorsFormValues?.investorsListData
			?.map((investor) => {
				const numberOfContracts = investor.contracts.length;
				const totalNumberOfShares = investor.securities.reduce(
					(acc, next) => acc + next.totalNumber,
					0
				);
				if (numberOfContracts > 0 && totalNumberOfShares === 0) {
					return false;
				}
				return true;
			})
			.reduce((acc, next) => acc && next, true);

		return result;
	})();

	const onComplete = async () => {
		if (isCompleting || !mipData) {
			return;
		}

		setIsCompleting(true);

		const generalValuesFormValues = generalDealInputsForm.getValues();
		const personalDataFormValues = personalDataForm.getValues();
		const kycFormValues = kycForm.getValues();
		const contractFormFormValues = contractForm.getValues();
		const inviteTemplateFormValues = inviteTemplateForm.getValues();
		const investorsFormValues = investorsForm.getValues();
		const loansDefinitionFormValues = loanDefinitionForm.getValues();

		const individualCustomDetails: CreateCustomDetailDto[] = [
			...personalDataFormValues.individual.map((customDetail) => ({
				...customDetail,
				fileId: undefined,
			})),
			...(kycFormValues.individualFormsTemplates
				?.filter((f) => f.local)
				.map((f) => ({
					name: f.name,
					instruction: f.instruction ?? '',
					fileId: f.fileId,
					type: f.type,
					dataType: CustomDetailDataTypeEnum.FORM,
				})) ?? []),
		];
		const pivCustomDetails: CreateCustomDetailDto[] = [
			...personalDataFormValues.vehicle.map((customDetail) => ({
				...customDetail,
				fileId: undefined,
			})),
			...(kycFormValues.pivFormsTemplates
				?.filter((f) => f.local)
				.map((f) => ({
					name: f.name,
					instruction: f.instruction ?? '',
					fileId: f.fileId,
					type: f.type,
					dataType: CustomDetailDataTypeEnum.FORM,
				})) ?? []),
		];

		const contractsWithFiles = await Promise.all(
			(contractFormFormValues?.contracts ?? []).map((item) =>
				uploadConcurrencyLimit(async () => {
					try {
						if (!item.fileInstance) {
							return item;
						}

						const formData = new FormData();
						formData.append('file', item.fileInstance);
						formData.append('fileSize', item.fileInstance.size.toString());

						const contract = await uploadFileRequest
							.fetch({ formData })
							.concludeWithPromise();
						if (!contract?.payload) {
							return item;
						}

						return {
							...item,
							file: contract.payload,
						};
					} catch (err) {
						logger.error('Upload error', err);
						return item;
					}
				})
			)
		);

		// Saving the joiner step writes the programme's own proof requirements, so it must not run
		// on values the modal never loaded — the form's mount defaults are all off.
		const proofUpdate = toProgrammeProofUpdate(programmeProofsStatus, kycFormValues);
		if (proofUpdate) {
			await putMipKycSettingRequest.fetch(proofUpdate).concludeWithPromise();
		} else {
			logger.error('Skipped saving joiner KYC proofs: the programme settings never loaded');
		}

		const securitiesToCreate = generalValuesFormValues.securities.filter(
			({ locked }) => !locked
		);

		await postJoinersOrAllocationsDataRequest
			.fetch({
				securitiesDto: {
					issuerId: mipData.entityId,
					securities: securitiesToCreate.map((security) => ({
						name: security.name,
						type: security.type,
						currency: mipData.currency,
						votesPerUnit: security.votesPerUnit ? Number(security.votesPerUnit) : null,
						nominalValue: security.nominalValue ? Number(security.nominalValue) : null,
						securityProgrammeSettings: {
							mipId: mipData.id,
							isSubjectToVesting: security.isSubjectToVesting,
						},
					})),
				},
				loansDefinitions: loansDefinitionFormValues?.[
					LoanDefinitionFormValues.loanDefinitions
				]
					.filter(({ locked }) => !locked)
					.map((loan) => ({
						type: loan.type,
						name: loan.name,
						funderPersonId: loan.funderIsNew ? undefined : loan.funderId,
						funderPersonNameToCreate: loan.funderIsNew ? loan.funderName : undefined,
						mipId: mipData.id,
					})),
				customDetails: {
					individual: individualCustomDetails,
					vehicle: pivCustomDetails,
				},
				contracts: contractsWithFiles.map((contract) => ({
					...contract,
					requiredWitnesses: contract.hasWitnesses ? 1 : 0,
					counterparties: isReviewSignatureType(contract.signatureType)
						? []
						: contract.counterparties,
				})),
				inviteTemplate: Object.values(inviteTemplateFormValues).length
					? inviteTemplateFormValues
					: undefined,
				investorAllocations: investorsFormValues?.investorsListData.map((data) => ({
					...data,
					vestingTermId: data?.securities?.some((s) => s.isSubjectToVesting)
						? data?.vestingTermId
						: undefined,
				})),
			})
			.onSuccess((msg) => {
				showToastV2({
					title: tMipManage(`new_joiner.toast.complete.success`),
				});

				setIsCompleting(false);
				startEmailWorkflow(msg.payload?.postJoinerAllocationsWorkflowHandle);
			})
			.onUnsafeFinish(() => {
				manageInvestorsControllerV2.refreshDataWithoutProgrammeFilter();
				manageInvestorsControllerV2.setShowNewJoinersAndAllocationsModal(false);

				setIsCompleting(false);
			})
			.concludeWithPromise();
	};

	const isNewJoinersModal = useMemo(
		() => manageInvestorsControllerV2.investorSharesModalMode === SHARES_MODAL_MODE.NEW_JOINERS,
		[manageInvestorsControllerV2.investorSharesModalMode]
	);

	const onNextStepClicked = () => {
		if (currentStep >= NEW_JOINERS_AND_ALLOCATIONS_MAX_STEP_NUMBER) {
			return;
		}
		if (
			(currentStep === 2 && !MIPManageController.mipInvestorSettings?.isKycRequired) ||
			(currentStep === 5 && isNewJoinersModal)
		) {
			setCurrentStep(currentStep + 2);
			return;
		}
		setCurrentStep(currentStep + 1);
	};

	const onPreviousStepClicked = () => {
		if (currentStep <= 0) {
			return;
		}
		if (
			(currentStep === 4 && !MIPManageController.mipInvestorSettings?.isKycRequired) ||
			(currentStep === 7 && isNewJoinersModal)
		) {
			setCurrentStep(currentStep - 2);
			return;
		}
		setCurrentStep(currentStep - 1);
	};

	/**
	 * Both the "New Joiners" modal and the "Add Allocations" modal uses this controller, as they share many pages in common.
	 * However, the "New Joiners" modal does not need the InvitationPreview page, and we don't need the KYC page if the programme doesn't have a KYC.
	 * The code above acts like a state machine. The variable currentStep keeps track of the state number, and we skip a state number (i.e. page)
	 * when we don't need a particular page. If we display the currentStep as the page number to the users, it would be very confusing, as you may see "Page 4" jumping
	 * to "Page 6" directly for the "New Joiners" modal. The variable is also zero-indexed, so user may see things like "Page 0 of N".
	 * To avoid such confusion, I wrote the following variables to keep track of the actual total number of pages, and
	 * the actual page number.
	 * TODO: The whole skip-page thing is really hacky and unintuitive. Preferably we would like to rewrite the whole mechanism.
	 */
	const calculateTotalSkippedStepsOneIndexed = () => {
		let total = 1; // We must skip the last confirmation page.
		if (isNewJoinersModal) {
			total += 1; // Skip the InvitationPreview form if the modal is for new joiners.
		}
		if (!MIPManageController.mipInvestorSettings?.isKycRequired) {
			total += 1; // Skip the KYC page if KYC is not required.
		}
		return total - 1; // Remove one here to avoid zero-indexing.
	};
	const totalStepsToDisplay =
		NEW_JOINERS_AND_ALLOCATIONS_MAX_STEP_NUMBER - calculateTotalSkippedStepsOneIndexed();
	const calculateSkippedStepsOneIndexed = () => {
		let skippedSteps = 0;

		if (currentStep >= 4 && !MIPManageController.mipInvestorSettings?.isKycRequired) {
			// If KYC is not required and we are at step 4+, we skipped the KYC page.
			skippedSteps++;
		}

		if (currentStep >= 7 && isNewJoinersModal) {
			// If the modal is for new joiners, and we are at step 7+, we skipped the InvitationPreview form.
			skippedSteps++;
		}

		return skippedSteps - 1; // Remove one here to avoid zero-indexing.
	};
	const currentStepToDisplay = currentStep - calculateSkippedStepsOneIndexed();

	const mipData = getMIPDetailsDataRequest.response?.payload;
	const allContracts = [
		...(mipData?.contractFiles ?? []),
		...(contractForm.getValues().contracts ?? []),
	];

	const allSecurities = useMemo(() => {
		return (
			generalDealInputsForm.getValues()?.securities?.map((sec) => {
				return {
					...sec,
					nominalValue: sec.nominalValue ? Number(sec.nominalValue) : null,
					votesPerUnit: sec.votesPerUnit ? Number(sec.votesPerUnit) : null,
				};
			}) ?? []
		);
	}, [generalDealInputsForm.getValues()]);

	return {
		mipData,
		allSecurities,
		allContracts,
		generalDealInputsForm,
		personalDataForm,
		contractForm,
		inviteTemplateForm,
		investorsForm,
		currentStep,
		isFinalStep: currentStep === NEW_JOINERS_AND_ALLOCATIONS_MAX_STEP_NUMBER,
		onNextStepClicked,
		onPreviousStepClicked,
		setDisableContinue,
		disableContinue,
		setShowNewJoinersAndAllocationsModal:
			manageInvestorsControllerV2.setShowNewJoinersAndAllocationsModal,
		onComplete,
		isNewJoinersModal,
		isCompleting,
		kycForm,
		programmeProofsStatus,
		loanDefinitionForm,
		allLoansDefinitions: loanDefinitionForm.getValues(LoanDefinitionFormValues.loanDefinitions),
		isMIP1200CaseValid,
		totalStepsToDisplay,
		currentStepToDisplay,
	};
};
